// Port of ios/Sources/Design/AvatarIdentity.swift (+ `Theme.avatarPalette` / `Theme.avatarText`
// from ios/Sources/Design/Theme.swift).
//
// Deterministic colour + initials for a user with no avatar (docs/DESIGN.md: "a missing avatar
// must still look intentional"). Pure and side-effect free — no DOM dependency — so it can be
// unit tested without a theme, a view, or a running app.

/**
 * A fixed set of identity colours for the avatar-initials placeholder (docs/VISUAL-DESIGN.md
 * §9's "a missing avatar must still look intentional"), indexed by `paletteIndex`. Deliberately
 * colour-scheme-INDEPENDENT — an avatar's identity colour is about who the person is, not the
 * app's current theme, so it never flips between light and dark for the same user. Same hexes,
 * same order as `Theme.avatarPalette`: an index means the same colour on every client.
 */
const palette: readonly string[] = [
  '#DB575C',
  '#DB8C3D',
  '#CCAD33',
  '#5CA862',
  '#429E9E',
  '#4D85DB',
  '#856BDB',
  '#CC5C9E',
];

/**
 * Fixed white, independent of theme — the initials placeholder's text always sits on a
 * mid-toned `palette` colour, never on `accent`, so it must not follow `onAccent`
 * (which flips to near-black in dark mode to sit on the LIGHT dark-mode accent instead).
 */
const textColor = '#FFFFFF';

/**
 * FNV-1a, 32-bit, over the UTF-8 bytes. Deterministic across process runs — unlike Swift's
 * `String.hashValue`/`Hasher`, which is seeded randomly PER PROCESS LAUNCH for hash-flooding
 * resistance and would make a user's placeholder colour change every time the app relaunches.
 * That randomization is exactly the trap docs/SLICE-D.md's "initials placeholder is deterministic
 * for a given user id" test exists to catch. UTF-8 (not UTF-16 code units) so a non-ASCII id
 * hashes to the same colour here as on the Apple clients.
 */
function stableHash(value: string): number {
  let hash = 2_166_136_261;
  for (const byte of new TextEncoder().encode(value)) {
    hash ^= byte;
    hash = Math.imul(hash, 16_777_619) >>> 0;
  }
  return hash >>> 0;
}

/**
 * An index into whatever fixed-size palette the caller provides — returned as a plain number
 * rather than a colour so this stays free of any UI/theme dependency and is trivially testable.
 */
function paletteIndex(userId: string, paletteCount: number): number {
  if (paletteCount <= 0) return 0;
  return stableHash(userId) % paletteCount;
}

/** The placeholder colour (hex) for `userId`, from `palette`. */
function color(userId: string): string {
  return palette[paletteIndex(userId, palette.length)];
}

/**
 * Up to two letters, derived from an already-resolved display name (nickname/full-name/
 * username priority — see `userDisplayName`), never from a raw id.
 * Two words → each word's first letter; one word → its first two letters; empty → "?".
 * Letters are whole grapheme clusters, as Swift's `Character` is.
 */
function initials(displayName: string): string {
  const words = displayName.split(' ').filter((w) => w !== '');
  if (words.length >= 2) {
    const first = graphemes(words[0])[0];
    const second = graphemes(words[1])[0];
    if (first && second) return (first + second).toUpperCase();
  }
  if (words.length > 0) return graphemes(words[0]).slice(0, 2).join('').toUpperCase();
  return '?';
}

const segmenter = typeof Intl !== 'undefined' && 'Segmenter' in Intl ? new Intl.Segmenter(undefined, { granularity: 'grapheme' }) : null;

function graphemes(s: string): string[] {
  if (segmenter) return Array.from(segmenter.segment(s), (x) => x.segment);
  return Array.from(s);
}

export const AvatarIdentity = {
  palette,
  textColor,
  stableHash,
  paletteIndex,
  color,
  initials,
} as const;
